import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import Head from 'next/head';
import Layout from '@/layouts';
import { AppSettings } from '@/shared/app-common';
import { getActivePageContentApi, getStartDataDetailApi, getStartDataListApiInner } from '@/lib/service';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page3JsonLd } from '@/lib/jsonld';
import { hyphenatedToTitleCase } from '@/shared/path-helper';

const StartInfo = dynamic(() => import('@/components/starInfo/index'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl, params } = context;
  const { id = '' } = params;

  const [starInfo, showInfo, localeRes, listRes, tdkRes] = await Promise.all([
    getStartDataDetailApi(id, locale),
    getActivePageContentApi(),
    serverSideTranslations(locale, ['common']),
    getStartDataListApiInner(
      {
        page_no: 1,
        page_size: 4,
        order_column: 'latest',
      },
      locale
    ),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;

  const jsonLd = page3JsonLd(
    {
      title: hyphenatedToTitleCase(`${id}`),
      desc: tdkRes?.data?.description ?? tempData['StarDataFilterDesc'].replace('xxx', id),
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Top Instagram',
      url: siteUrl + '/top-instagram',
    },
    {
      url: siteUrl,
    }
  );
  // Redirect to /top-instagram if starInfo is not found
  if (starInfo?.code !== 0) {
    return {
      redirect: {
        destination: locale === 'en' ? `/top-instagram` : `/${locale}/top-instagram`,
        permanent: true,
      },
    };
  }

  return {
    props: {
      ...localeRes,
      StarInfo: { ...(starInfo ?? {}) },
      showInfo: { ...(showInfo ?? {}) },
      listData: listRes?.data ?? [],
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData['head_starData_Meta_Title'].replace('xxx', id),
      description: tdkRes?.data?.description ?? tempData['StarDataFilterDesc'].replace('xxx', id),
      jsonLd,
    },
  };
};

const CategoryPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return useMemo(() => <StartInfo data={props} />, [props]);
};

CategoryPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />

          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default CategoryPage;
